import { useQuery } from '@tanstack/react-query';
import { pb, TutorProfile } from '@/lib/pocketbase';
import { normalizeTutorSubjects } from '@/utils/tutorDataUtils';
import { translateSearchQuery } from '@/utils/subjectMapping';

export const useTutors = () => {
  const query = useQuery({
    queryKey: ['tutors-v2'], // Changed query key to force refetch
    queryFn: async ({ signal }) => {
      try {
        const tutors = await pb.collection('tutors').getList(1, 200, {
          expand: 'user',
          sort: '-created',
          filter: 'profilePicture != "" && is_suspended != true'
        });
        
        return tutors.items.map(normalizeTutorSubjects);
      } catch (error: any) {
        // Don't log auto-cancellation errors as they're expected behavior
        if (error.status !== 0) {
          
          // Try without auth if the error is auth-related
          if (error.status === 403 || error.status === 401) {
            try {
              const publicTutors = await pb.collection('tutors').getList(1, 200, {
                sort: '-created',
                filter: 'profilePicture != "" && is_suspended != true',
                requestKey: null,
              });
              return publicTutors.items.map(normalizeTutorSubjects);
            } catch (publicError) {
            }
          }
        }
        return [];
      }
    },
    refetchOnWindowFocus: false,
    staleTime: 30000,
    retry: (failureCount, error: any) => {
      // Don't retry auto-cancellation errors
      if (error?.status === 0) {
        return false;
      }
      return failureCount < 2; // Allow one more retry
    },
  });

  const searchTutors = async (filters: {
    searchQuery: string;
    selectedSubject: string;
    selectedCountry: string;
    priceRange: string;
    availability: string;
  }) => {
    try {
      // Debug: Check total English tutors without filters
      if (filters.searchQuery?.toLowerCase().includes('english')) {
        try {
          const allEnglish = await pb.collection('tutors').getList(1, 200, {
            filter: 'subjects ~ "English"',
            requestKey: null
          });
          const withPhoto = allEnglish.items.filter(t => t.profilePicture && t.profilePicture.trim() !== '');
          const notSuspended = withPhoto.filter(t => !t.is_suspended);
          const notHidden = notSuspended.filter(t => t.is_displayed !== false);
          const notFrozen = notHidden.filter(t => !t.isFrozen);
          
          console.log('📊 English tutors breakdown:');
          console.log('  - Total with "English" in subjects:', allEnglish.items.length);
          console.log('  - With profile picture:', withPhoto.length);
          console.log('  - Not suspended:', notSuspended.length);
          console.log('  - Not hidden:', notHidden.length);
          console.log('  - Not frozen (final):', notFrozen.length);
          
          // Log tutors without photos
          const noPhoto = allEnglish.items.filter(t => !t.profilePicture || t.profilePicture.trim() === '');
          if (noPhoto.length > 0) {
            console.log('  - Tutors WITHOUT photos:', noPhoto.map(t => t.name || t.id));
          }
        } catch (e) {
          console.log('Debug query failed:', e);
        }
      }
      
      let filterString = '';
      const filterParts = [];

      // Handle search query - search in tutor's name, user's name, and subjects
      if (filters.searchQuery && filters.searchQuery.trim() !== '') {
        const searchTerm = translateSearchQuery(filters.searchQuery.trim());
        
        // Expand English-related searches to include all related subjects
        const englishRelatedSubjects = ['English', 'ESL', 'EFL', 'English Literature', 'Creative Writing', 'TOEFL', 'IELTS', 'Business English', 'General English', 'Conversation', 'English Language'];
        const isEnglishSearch = searchTerm.toLowerCase() === 'english' || 
                                searchTerm.toLowerCase().includes('english') ||
                                englishRelatedSubjects.some(s => s.toLowerCase() === searchTerm.toLowerCase());
        
        const searchConditions = [
          `name ~ "${searchTerm}"`,
          `user.name ~ "${searchTerm}"`
        ];
        
        if (isEnglishSearch) {
          // Search for all English-related subjects
          for (const subject of englishRelatedSubjects) {
            searchConditions.push(`subjects ~ "${subject}"`);
          }
        } else {
          // Normal subject search
          searchConditions.push(`subjects ~ "${searchTerm}"`);
          searchConditions.push(`subjects ~ '"${searchTerm}"'`);
          searchConditions.push(`subjects ~ '"${searchTerm},"'`);
          searchConditions.push(`subjects ~ ',"${searchTerm}"'`);
        }
        
        filterParts.push(`(${searchConditions.join(' || ')})`);
      }

      // Handle subject filter - only if it's not 'All Subjects' and not empty
      if (filters.selectedSubject && 
          filters.selectedSubject !== 'All Subjects' && 
          filters.selectedSubject.trim() !== '') {
        const subjectFilter = filters.selectedSubject.trim();
        console.log('🔍 Searching for subject:', subjectFilter);
        
        // For JSON array fields in PocketBase, we need to search within the serialized JSON
        // Try multiple approaches to match the subject
        const subjectConditions = [
          `subjects ~ "${subjectFilter}"`,  // Contains the subject name
          `subjects ~ '"${subjectFilter}"'`, // Exact match with quotes (JSON format)
          `subjects ~ '"${subjectFilter},"'`, // With comma (middle of array)
          `subjects ~ ',"${subjectFilter}"'`, // With leading comma
          `subjects ~ '["${subjectFilter}"]'` // Single item array
        ];
        
        filterParts.push(`(${subjectConditions.join(' || ')})`);
      }

      // Handle country filter
      if (filters.selectedCountry && filters.selectedCountry.trim() !== '') {
        const countryCode = filters.selectedCountry.trim().toUpperCase();
        console.log('Filtering by country:', countryCode);
        filterParts.push(`(country = "${countryCode}" || heroCountries ~ "${countryCode}")`);
      }

      // Add mandatory filters for photo and active status
      filterParts.push('profilePicture != ""');
      filterParts.push('is_suspended != true');

      // Combine all filter parts with AND operator
      filterString = filterParts.join(' && ');

      const tutors = await pb.collection('tutors').getList(1, 200, {
        filter: filterString,
        expand: 'user',
        sort: '-created',
        fields: '*,expand.user.*', // Ensure all fields including isFeatured are fetched
        requestKey: null, // Disable auto-cancellation for search requests
      });

      const normalizedTutors = tutors.items.map(normalizeTutorSubjects);
      
      console.log('🔍 Search results count:', normalizedTutors.length);
      console.log('🔍 Filter string used:', filterString);

      return normalizedTutors;
    } catch (error: any) {
      // Don't log expected auth warnings for public data
      if (error.status !== 0 && error.status !== 403 && error.status !== 401) {
        console.error('Error searching tutors:', error);
      }
      return [];
    }
  };

  return {
    tutors: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    searchTutors,
    refreshTutors: query.refetch,
  };
};
